import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const QuickAddActivity = ({ isOpen, onClose, onAdd, initialData }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    type: 'task',
    title: '',
    description: '',
    dueDate: '',
    dueTime: '',
    priority: 'medium',
    owner: '',
    contact: '',
    account: '',
    notes: '',
    reminderEnabled: false,
    reminderTime: '15'
  });

  const [errors, setErrors] = useState({});
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch users, contacts, and accounts from database
  useEffect(() => {
    if (isOpen) {
      fetchDropdownData();
    }
  }, [isOpen]);

  // Set initial data when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        type: initialData?.type || 'task',
        title: initialData?.title || '',
        description: initialData?.description || '',
        dueDate: initialData?.dueDate ? new Date(initialData.dueDate)?.toISOString()?.split('T')?.[0] : '',
        dueTime: initialData?.dueDate ? new Date(initialData.dueDate)?.toISOString()?.split('T')?.[1]?.substring(0, 5) : '',
        priority: initialData?.priority || 'medium',
        owner: initialData?.ownerId || '',
        contact: initialData?.contactId || '',
        account: initialData?.accountId || '',
        notes: initialData?.notes || '',
        reminderEnabled: initialData?.reminderEnabled || false,
        reminderTime: initialData?.reminderTime || '15'
      });
    }
  }, [initialData]);

  const fetchDropdownData = async () => {
    try {
      setLoading(true);
      
      // Fetch users from user_profiles
      const { data: usersData, error: usersError } = await supabase?.from('user_profiles')?.select('id, first_name, last_name, email')?.eq('tenant_id', user?.tenantId)?.eq('is_active', true)?.order('first_name');

      if (usersError) throw usersError;

      // Fetch contacts from customers
      const { data: contactsData, error: contactsError } = await supabase?.from('customers')?.select('id, contact_person, company_name, email')?.eq('tenant_id', user?.tenantId)?.eq('is_active', true)?.order('contact_person');

      if (contactsError) throw contactsError;

      // Fetch accounts (unique company names from customers)
      const { data: accountsData, error: accountsError } = await supabase?.from('customers')?.select('id, company_name')?.eq('tenant_id', user?.tenantId)?.eq('is_active', true)?.not('company_name', 'is', null)?.order('company_name');

      if (accountsError) throw accountsError;

      // Remove duplicate company names
      const uniqueAccounts = accountsData?.reduce((acc, current) => {
        const exists = acc?.find(item => item?.company_name === current?.company_name);
        if (!exists) {
          acc?.push(current);
        }
        return acc;
      }, []);

      setUsers(usersData || []);
      setContacts(contactsData || []);
      setAccounts(uniqueAccounts || []);

      // Set default owner to current user if not editing
      if (!initialData && usersData?.length > 0) {
        const currentUser = usersData?.find(u => u?.id === user?.id);
        if (currentUser) {
          setFormData(prev => ({ ...prev, owner: currentUser?.id }));
        }
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    } finally {
      setLoading(false);
    }
  };

  const activityTypeOptions = [
    { value: 'task', label: 'Task' },
    { value: 'call', label: 'Call' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'email', label: 'Email' },
    { value: 'note', label: 'Note' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'high', label: 'High Priority' }
  ];

  // Convert users to options format
  const ownerOptions = users?.map(user => ({
    value: user?.id,
    label: `${user?.first_name || ''} ${user?.last_name || ''}`?.trim() || user?.email
  })) || [];

  // Convert contacts to options format
  const contactOptions = [
    { value: '', label: 'Select Contact (Optional)' },
    ...(contacts?.map(contact => ({
      value: contact?.id,
      label: `${contact?.contact_person || 'Unknown'} - ${contact?.company_name || 'No Company'}`
    })) || [])
  ];

  // Convert accounts to options format
  const accountOptions = [
    { value: '', label: 'Select Account (Optional)' },
    ...(accounts?.map(account => ({
      value: account?.id,
      label: account?.company_name
    })) || [])
  ];

  const reminderOptions = [
    { value: '5', label: '5 minutes before' },
    { value: '15', label: '15 minutes before' },
    { value: '30', label: '30 minutes before' },
    { value: '60', label: '1 hour before' },
    { value: '1440', label: '1 day before' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.title?.trim()) {
      newErrors.title = 'Activity title is required';
    }
    
    if (!formData?.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }
    
    if (!formData?.dueTime) {
      newErrors.dueTime = 'Due time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Find selected user, contact, and account details
    const selectedUser = users?.find(u => u?.id === formData?.owner);
    const selectedContact = contacts?.find(c => c?.id === formData?.contact);
    const selectedAccount = accounts?.find(a => a?.id === formData?.account);

    const newActivity = {
      id: initialData?.id || Date.now(),
      type: formData?.type,
      title: formData?.title,
      description: formData?.description,
      dueDate: new Date(`${formData.dueDate}T${formData.dueTime}`),
      priority: formData?.priority,
      owner: selectedUser ? `${selectedUser?.first_name || ''} ${selectedUser?.last_name || ''}`?.trim() : '',
      ownerId: formData?.owner,
      contact: selectedContact?.contact_person || null,
      contactId: formData?.contact || null,
      account: selectedAccount?.company_name || null,
      accountId: formData?.account || null,
      notes: formData?.notes,
      completed: initialData?.completed || false,
      reminderEnabled: formData?.reminderEnabled,
      reminderTime: formData?.reminderTime
    };

    onAdd(newActivity);
    handleReset();
    onClose();
  };

  const handleReset = () => {
    setFormData({
      type: 'task',
      title: '',
      description: '',
      dueDate: '',
      dueTime: '',
      priority: 'medium',
      owner: '',
      contact: '',
      account: '',
      notes: '',
      reminderEnabled: false,
      reminderTime: '15'
    });
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-background border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Plus" size={20} className="text-primary" />
            <h2 className="text-lg font-semibold text-foreground">
              {initialData ? 'Edit Activity' : 'Quick Add Activity'}
            </h2>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close dialog"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <Icon name="Loader" size={24} className="animate-spin mx-auto text-primary" />
            <p className="mt-2 text-muted-foreground">Loading...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Activity Type & Priority */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Activity Type"
                options={activityTypeOptions}
                value={formData?.type}
                onChange={(value) => handleInputChange('type', value)}
                required
              />
              
              <Select
                label="Priority"
                options={priorityOptions}
                value={formData?.priority}
                onChange={(value) => handleInputChange('priority', value)}
                required
              />
            </div>

            {/* Title */}
            <Input
              label="Activity Title"
              type="text"
              placeholder="Enter activity title"
              value={formData?.title}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
              error={errors?.title}
              required
            />

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={3}
                placeholder="Enter activity description (optional)"
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
              />
            </div>

            {/* Due Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Due Date"
                type="date"
                value={formData?.dueDate}
                onChange={(e) => handleInputChange('dueDate', e?.target?.value)}
                error={errors?.dueDate}
                required
              />
              
              <Input
                label="Due Time"
                type="time"
                value={formData?.dueTime}
                onChange={(e) => handleInputChange('dueTime', e?.target?.value)}
                error={errors?.dueTime}
                required
              />
            </div>

            {/* Owner */}
            <Select
              label="Assigned To"
              options={ownerOptions}
              value={formData?.owner}
              onChange={(value) => handleInputChange('owner', value)}
              required
              searchable
            />

            {/* Contact & Account */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Related Contact"
                options={contactOptions}
                value={formData?.contact}
                onChange={(value) => handleInputChange('contact', value)}
                searchable
              />
              
              <Select
                label="Related Account"
                options={accountOptions}
                value={formData?.account}
                onChange={(value) => handleInputChange('account', value)}
                searchable
              />
            </div>

            {/* Reminder Settings */}
            <div className="space-y-4">
              <Checkbox
                label="Set Reminder"
                checked={formData?.reminderEnabled}
                onChange={(e) => handleInputChange('reminderEnabled', e?.target?.checked)}
              />
              
              {formData?.reminderEnabled && (
                <Select
                  label="Reminder Time"
                  options={reminderOptions}
                  value={formData?.reminderTime}
                  onChange={(value) => handleInputChange('reminderTime', value)}
                />
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Additional Notes
              </label>
              <textarea
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                rows={3}
                placeholder="Add any additional notes (optional)"
                value={formData?.notes}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              
              <Button
                type="button"
                variant="ghost"
                onClick={handleReset}
              >
                Reset
              </Button>
              
              <Button
                type="submit"
                variant="default"
                iconName="Plus"
                iconPosition="left"
              >
                {initialData ? 'Update Activity' : 'Add Activity'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default QuickAddActivity;